import { Injectable, Inject } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { EnvironmentConfig } from '@examsoft/haste-ui';

export interface ILaunchDarklyService {
  initialize(sdkKey?: string): Promise<LaunchDarklyService>;
  getFeatureFlag<T = any>(flagKey: string, defaultValue: T): T;
  getFeatureFlagByRegion(featureFlagKey: string): boolean;
}

@Injectable({ providedIn: 'root' })
export class LaunchDarklyService implements ILaunchDarklyService {
  private client: LDClient.LDClient | null = null;

  async initialize(sdkKey?: string): Promise<LaunchDarklyService> {
    const key = sdkKey;
    if (!key) {
      console.warn('LaunchDarkly SDK key not provided; skipping initialization.');
      return;
    }

    const context: LDClient.LDContext = { key: 'user-key', anonymous: true };
    const logger: LDClient.LDLogger = LDClient.basicLogger({ level: 'warn' });
    const options: LDClient.LDOptions = { streaming: true, bootstrap: 'localStorage', logger };

    this.client = LDClient.initialize(key, context, options);

    this.client.on('change', (allChanges: any) => {
      console.debug(`Configuration changes triggered :: ${JSON.stringify(allChanges)}`);
    });

    try {
      await this.client.waitUntilReady();
    } catch (err) {
      console.warn('LaunchDarkly client failed to become ready', err);
    }

    return this;
  }

  getFeatureFlag<T = any>(flagKey: string, defaultValue: T): T {
    if (this.client) {
      try {
        return (this.client.variation(flagKey, defaultValue) as unknown) as T;
      } catch (err) {
        console.warn(`Error reading feature flag ${flagKey}`, err);
        return defaultValue;
      }
    } else {
      console.warn('LaunchDarkly client not initialized. Returning default value.');
      return defaultValue;
    }
  }

  getFeatureFlagByRegion(featureFlagKey: string): boolean {
    let isActive = false;

    try {
      const region = EnvironmentConfig.getData().serverlessWebSocket?.region;
      const flagValue = this.getFeatureFlag<{ enabled_regions?: string[] }>(featureFlagKey, {
        enabled_regions: [],
      });
      const enabledRegions = flagValue?.enabled_regions || [];
      isActive = !!enabledRegions.includes(region);
    } catch (err) {
      console.warn(`An error occurred while evaluating configuration flag`, err);
    }

    return isActive;
  }
}
